import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { REVIEW_API, type Review } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { GameBar } from './GameBar.tsx';
import { ownBlunders, summaryOf } from './summary.ts';
import { backButton } from './back.ts';
import './game-bar.css';
import { STORE_API, type GameRecord, type GameStore } from '@chess/api-model';
import { useStoreState } from '@chess/api-model/react';
import { endingEntryPly, endingOf } from './ending.ts';
import { usePuzzleRun } from './use-puzzles.ts';

// The game bar across the top of the review: a back button to the screen the
// review was opened from (Home when there is none), who played, the result, how it
// ended, the particulars behind "More", and a link back to the source, all
// from the open game's stored record, and after the result how the reader's
// ending went (the Ending cards' case, from the store's facts), a chip that
// moves the board to the ending's first position. The record is read once per game;
// the move count and the book's name for the opening come from the session.
// While a puzzle is on the board (`puzzles.current()`) the row is the
// puzzle's banner instead, filled by the puzzles plugin through
// `review.header.puzzle`: the puzzle, not the game, is what the reader is on.

interface RecordState {
  gameId?: string;
  record?: GameRecord;
}
const useRecord = create<RecordState>(() => ({}));

export function setup(app: PiletApi) {
  let review: Review | undefined;
  let games: GameStore | undefined;
  let loading: string | undefined;

  const load = async (gameId: string | undefined) => {
    if (gameId === loading) return;
    loading = gameId;
    if (!gameId || !games) {
      useRecord.setState({ gameId, record: undefined });
      return;
    }
    useRecord.setState({ gameId, record: undefined });
    const record = await games.record(gameId).catch(() => undefined);
    if (loading === gameId) useRecord.setState({ gameId, record });
  };

  const getApi = <T,>(name: string) => app.getApi<T>(name);

  const Bar: React.FC = () => {
    const moveCount = useSelect(review, (s) => s.game?.moves.length ?? 0) ?? 0;
    const opening = useSelect(review, (s) => s.book?.reading?.opening.name);
    const blunders = useSelect(review, (s) => (s.game ? ownBlunders(s.scan, s.game.moves, s.reader) : undefined));
    const scanning = useSelect(review, (s) => !!s.scanning) ?? false;
    const gameId = useSelect(review, (s) => s.gameId);
    const reached = useSelect(review, (s) => s.game?.phases?.endgameStart !== undefined) ?? false;
    const scanned = useSelect(review, (s) => !!s.scan) ?? false;
    const { state: store } = useStoreState(getApi);
    const { record } = useRecord();
    const navigate = useNavigate();
    const puzzle = usePuzzleRun(app);
    // Every hook is above this return, so the hook order never changes.
    if (puzzle) return <app.Extension name="review.header.puzzle" />;
    if (!record) return null;
    // The history state is read at render; see back.ts.
    const back = backButton(window.history.state, (to) => (typeof to === 'number' ? navigate(to) : navigate(to)));
    const stored = gameId ? store?.games.find((g) => g.summary.id === gameId) : undefined;
    const kind = endingOf({ reached, scanned, status: store?.status, stored });
    const ending =
      kind === 'loading' || kind === undefined
        ? kind
        : {
            case: kind,
            // The ending's first move, where a click on the Ending tab lands. See ending.ts.
            onOpen: () => {
              const ply = review ? endingEntryPly(review.state()) : undefined;
              if (ply !== undefined) review!.go(ply);
            },
          };
    return <GameBar summary={summaryOf(record, moveCount, opening)} blunders={blunders} scanning={scanning} back={back} ending={ending} />;
  };

  void Promise.all([app.getApi<Review>(REVIEW_API), app.getApi<GameStore>(STORE_API)]).then(([r, g]) => {
    review = r;
    games = g;
    r.subscribe((s) => void load(s.gameId));
    void load(r.state().gameId);
  });

  app.registerExtension('review.header', () => <Bar />);
}
